import React, { useState, useEffect, useCallback } from "react";
import API_BASE_URL from "./RestAPI";
import { showSuccessToast, showErrorToast } from "../constants/ToastService.js";
import { invalidateRequirementsCache } from "./Common Components/requirementsCache";
import AppTooltip from "./Common Components/AppTooltip";
import "./LiveMoveRequests.css";

/**
 * Move-to-Live / Release-Approval queue, grouped sprint-wise.
 *
 * A sprint can go live MULTIPLE times: each approval batch (set of requests
 * approved together) is shown as a separate "release round" under its sprint,
 * so the history of every go-live for a sprint is visible at a glance.
 *
 * Backend: GET  ticketing/live-move/?status=PENDING  (no ticket_id -> all requests)
 *          PUT  ticketing/live-move/        { id, decision, decision_note }   (single)
 *          PUT  ticketing/live-move/bulk/   { ids, decision, decision_note }  (bulk)
 */
const STATUS_FILTERS = ["PENDING", "APPROVED", "REJECTED", "ALL"];

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
});

const fmt = (s) => {
  if (!s) return "";
  try {
    return new Date(s).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(s);
  }
};

const ticketLabel = (r) => r.ticket_code || `Ticket #${r.ticket_id}`;

const LiveMoveRequests = () => {
  const userPermission = JSON.parse(
    localStorage.getItem("userPermissions") || "[]"
  );
  const canApprove = userPermission.includes("Approve Live Move");

  const featureEnabled =
    JSON.parse(localStorage.getItem("liveSupportEnabled") || "false") ||
    JSON.parse(localStorage.getItem("releaseApprovalEnabled") || "false");

  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);

  // Bulk approve/reject state.
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkNote, setBulkNote] = useState("");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const fetchRequests = useCallback(() => {
    setLoading(true);
    const query = statusFilter === "ALL" ? "" : `?status=${statusFilter}`;
    fetch(`${API_BASE_URL}ticketing/live-move/${query}`, {
      method: "GET",
      headers: authHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setRequests(Array.isArray(data.data) ? data.data : []);
        } else {
          showErrorToast(data.message || "Could not load live move requests");
        }
      })
      .catch((err) => {
        console.error("Failed to load live move requests:", err);
        showErrorToast("Could not load live move requests");
      })
      .finally(() => setLoading(false));
    setSelectedIds([]);
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // ── Selection / decisions ──────────────────────────────────────────────
  const pendingIds = requests
    .filter((r) => r.request_status === "PENDING")
    .map((r) => r.id);

  // While requests are picked, decisions go through the bulk bar only.
  const rowsLocked = selectedIds.length > 0;
  const ROW_LOCK_HINT = "Use Approve / Reject selected above, or clear the selection";

  const toggleSelect = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleSelectAll = () =>
    setSelectedIds((prev) =>
      prev.length === pendingIds.length ? [] : [...pendingIds]
    );

  const decide = (id, decision) => {
    setSubmittingId(id);
    fetch(`${API_BASE_URL}ticketing/live-move/`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({ id, decision, decision_note: "" }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          showSuccessToast(`Request ${decision.toLowerCase()}`);
          invalidateRequirementsCache(); // an approval can move its requirement to Production
          fetchRequests();
        } else {
          showErrorToast(data.message || "Could not update request");
        }
      })
      .catch(() => showErrorToast("Could not update request"))
      .finally(() => setSubmittingId(null));
  };

  const bulkDecide = (decision) => {
    if (selectedIds.length === 0) return;
    setBulkSubmitting(true);
    fetch(`${API_BASE_URL}ticketing/live-move/bulk/`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({ ids: selectedIds, decision, decision_note: bulkNote }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          showSuccessToast(data.message || `Requests ${decision.toLowerCase()}`);
          if (data.data?.skipped?.length) {
            console.warn("[Bulk decision] skipped:", data.data.skipped);
          }
          setBulkNote("");
          setSelectedIds([]);
          invalidateRequirementsCache();
          fetchRequests();
        } else {
          showErrorToast(data.message || "Could not update requests");
        }
      })
      .catch(() => showErrorToast("Could not update requests"))
      .finally(() => setBulkSubmitting(false));
  };

  // ── Group requests sprint-wise, then split into pending + release rounds ──
  const buildSprintGroups = () => {
    const groups = {};
    requests.forEach((r) => {
      const key = r.sprint_id == null ? "none" : String(r.sprint_id);
      if (!groups[key]) {
        groups[key] = {
          key,
          sprintId: r.sprint_id,
          sprintName:
            r.sprint_name ||
            (r.sprint_id ? `Sprint #${r.sprint_id}` : "No sprint"),
          all: [],
        };
      }
      groups[key].all.push(r);
    });

    return Object.values(groups).map((g) => {
      const pending = g.all.filter((r) => r.request_status === "PENDING");
      const rejected = g.all.filter((r) => r.request_status === "REJECTED");
      const approved = g.all.filter((r) => r.request_status === "APPROVED");

      // Release rounds: approvals sharing the same approved_on form one go-live.
      const roundMap = {};
      approved.forEach((r) => {
        const k = r.approved_on || "unknown";
        if (!roundMap[k]) {
          roundMap[k] = { key: k, approvedOn: r.approved_on, approver: r.approved_by, items: [] };
        }
        roundMap[k].items.push(r);
      });
      const rounds = Object.values(roundMap).sort(
        (a, b) => new Date(b.approvedOn || 0) - new Date(a.approvedOn || 0)
      );

      return { ...g, pending, rejected, rounds, liveCount: rounds.length };
    });
  };

  const sprintGroups = buildSprintGroups();

  // ── Render ──────────────────────────────────────────────────────────────
  if (!featureEnabled) {
    return (
      <div className="lm-page">
        <h2 className="lm-title">Move to Live</h2>
        <p className="lm-subtitle">
          The release-approval / Live Support feature is not enabled for this
          company.
        </p>
      </div>
    );
  }

  const renderRow = (r, { selectable } = {}) => (
    <div className="lm-row" key={r.id}>
      {selectable && canApprove && (
        <input
          type="checkbox"
          className="lm-row__check"
          checked={selectedIds.includes(r.id)}
          onChange={() => toggleSelect(r.id)}
        />
      )}
      <div className="lm-row__main">
        <div className="lm-row__title">
          <span className="lm-row__code">{ticketLabel(r)}</span>
          {r.ticket_title || ""}
        </div>
        <div className="lm-row__meta">
          Requested by <b>{r.requested_by || "—"}</b>
          {r.requested_on ? ` · ${fmt(r.requested_on)}` : ""}
          {r.ticket_status_at_request ? ` · status: ${r.ticket_status_at_request}` : ""}
        </div>
        {r.request_note && <div className="lm-row__note">“{r.request_note}”</div>}
        {(r.request_status === "APPROVED" || r.request_status === "REJECTED") && (
          <div className="lm-row__meta">
            {r.request_status === "APPROVED" ? "Approved" : "Rejected"} by{" "}
            <b>{r.approved_by || "—"}</b>
            {r.approved_on ? ` · ${fmt(r.approved_on)}` : ""}
            {r.decision_note ? ` — ${r.decision_note}` : ""}
          </div>
        )}
      </div>
      {selectable && canApprove ? (
        <div className="lm-row__actions">
          <AppTooltip title={rowsLocked ? ROW_LOCK_HINT : ""}>
            <span style={{ display: "inline-flex" }}>
              <button
                className="lm-btn lm-btn--approve"
                disabled={rowsLocked || submittingId === r.id}
                onClick={() => decide(r.id, "APPROVED")}
              >
                Approve
              </button>
            </span>
          </AppTooltip>
          <AppTooltip title={rowsLocked ? ROW_LOCK_HINT : ""}>
            <span style={{ display: "inline-flex" }}>
              <button
                className="lm-btn lm-btn--reject"
                disabled={rowsLocked || submittingId === r.id}
                onClick={() => decide(r.id, "REJECTED")}
              >
                Reject
              </button>
            </span>
          </AppTooltip>
        </div>
      ) : (
        <span className={`lm-pill lm-pill--${r.request_status.toLowerCase()}`}>
          {r.request_status}
        </span>
      )}
    </div>
  );

  return (
    <div className="lm-page">
      <div className="lm-header">
        <div>
          <h2 className="lm-title">Move to Live · Release Approvals</h2>
          <p className="lm-subtitle">
            Requests grouped by sprint. Each approval batch is a separate
            go-live, so a sprint can be released to live multiple times.
          </p>
        </div>
        <div className="lm-filters">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              className={`lm-filter ${statusFilter === s ? "lm-filter--active" : ""}`}
              onClick={() => setStatusFilter(s)}
            >
              {s.charAt(0) + s.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk action bar */}
      {canApprove && pendingIds.length > 0 && (
        <div className="lm-bulkbar">
          <div className="lm-bulkbar__top">
            <label className="lm-check">
              <input
                type="checkbox"
                checked={
                  selectedIds.length === pendingIds.length && pendingIds.length > 0
                }
                ref={(el) => {
                  if (el)
                    el.indeterminate =
                      selectedIds.length > 0 &&
                      selectedIds.length < pendingIds.length;
                }}
                onChange={toggleSelectAll}
              />
              Select all pending ({pendingIds.length})
            </label>
            <span className="lm-selcount">{selectedIds.length} selected</span>
          </div>
          <textarea
            rows={2}
            value={bulkNote}
            onChange={(e) => setBulkNote(e.target.value)}
            placeholder="Overall comment applied to all selected requests…"
            style={{ margin: "10px 0" }}
          />
          <div className="lm-actions">
            <button
              className="lm-btn lm-btn--approve"
              disabled={bulkSubmitting || selectedIds.length === 0}
              onClick={() => bulkDecide("APPROVED")}
            >
              Approve selected ({selectedIds.length})
            </button>
            <button
              className="lm-btn lm-btn--reject"
              disabled={bulkSubmitting || selectedIds.length === 0}
              onClick={() => bulkDecide("REJECTED")}
            >
              Reject selected ({selectedIds.length})
            </button>
          </div>
        </div>
      )}

      {loading && <p className="lm-loading">Loading…</p>}

      {!loading && sprintGroups.length === 0 && (
        <div className="lm-empty">
          No {statusFilter === "ALL" ? "" : statusFilter.toLowerCase() + " "}
          requests found.
        </div>
      )}

      {sprintGroups.map((g) => (
        <div className="lm-sprint" key={g.key}>
          <div className="lm-sprint__head">
            <div className="lm-sprint__name">{g.sprintName}</div>
            <div className="lm-sprint__chips">
              {g.pending.length > 0 && (
                <span className="lm-chip lm-chip--pending">
                  {g.pending.length} pending
                </span>
              )}
              {g.liveCount > 0 && (
                <span className="lm-chip lm-chip--live">
                  Went live {g.liveCount}×
                </span>
              )}
              {g.rejected.length > 0 && (
                <span className="lm-chip lm-chip--rejected">
                  {g.rejected.length} rejected
                </span>
              )}
            </div>
          </div>

          <div className="lm-sprint__body">
            {/* Pending */}
            {g.pending.length > 0 && (
              <>
                <div className="lm-section-label">Awaiting approval</div>
                {g.pending.map((r) => renderRow(r, { selectable: true }))}
              </>
            )}

            {/* Release rounds (each go-live) */}
            {g.rounds.length > 0 && (
              <>
                <div className="lm-section-label">
                  Release history ({g.rounds.length} go-live
                  {g.rounds.length > 1 ? "s" : ""})
                </div>
                {g.rounds.map((round, idx) => (
                  <details className="lm-round" key={round.key} open={idx === 0}>
                    <summary className="lm-round__summary">
                      <span className="lm-round__badge">
                        Release #{g.rounds.length - idx}
                      </span>
                      <span>
                        {round.items.length} ticket
                        {round.items.length > 1 ? "s" : ""} · {fmt(round.approvedOn)}
                      </span>
                      <span className="lm-selcount">by {round.approver || "—"}</span>
                    </summary>
                    <div className="lm-round__body">
                      {round.items.map((r) => renderRow(r))}
                    </div>
                  </details>
                ))}
              </>
            )}

            {/* Rejected (when shown) */}
            {g.rejected.length > 0 && (
              <>
                <div className="lm-section-label">Rejected</div>
                {g.rejected.map((r) => renderRow(r))}
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default LiveMoveRequests;
